import React, { useState, useEffect, useRef } from 'react';
import CommBroadcastPanel from './comm/CommBroadcastPanel';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { MessageCircle, Users, Send, Plus, Hash, User, ArrowLeft, Trash2, RefreshCw, Mail, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagingPanel({ orgId, userId, userName, userRole, teams }) {
  const { isDark } = useTheme();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('channels');
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelType, setNewChannelType] = useState('team');
  const [newChannelName, setNewChannelName] = useState('');
  const [orgMembers, setOrgMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [unread, setUnread] = useState({});
  const [showEmailBlast, setShowEmailBlast] = useState(false);
  const [emailBlast, setEmailBlast] = useState({ subject: '', content: '', target_type: 'org', target_id: '' });
  const [sendingBlast, setSendingBlast] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => { if (orgId) { fetchChannels(); fetchUnread(); connectWebSocket(); } 
    return () => { 
      if (pollRef.current) clearInterval(pollRef.current); 
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, [orgId]);

  const connectWebSocket = () => {
    try {
      let wsBase;
      if (/^https?:\/\//i.test(API_URL || '')) {
        wsBase = API_URL.replace(/^http/i, 'ws').replace(/\/+$/, '');
      } else {
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
        wsBase = `${proto}://${window.location.host}`;
      }
      const ws = new WebSocket(`${wsBase}/api/ws/notifications/${orgId}?user_id=${userId}`);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            // If we're viewing this channel, refresh messages instantly
            if (activeChannelRef.current?.id === data.channel_id) {
              fetchMessages(data.channel_id);
            } else {
              // Update unread for other channels
              setUnread(prev => ({ ...prev, [data.channel_id]: (prev[data.channel_id] || 0) + 1 }));
              fetchChannels();
            }
          }
        } catch (e) { /* ignore parse errors */ }
      };
      ws.onclose = () => { setTimeout(() => { if (orgId) connectWebSocket(); }, 5000); };
      wsRef.current = ws;
    } catch (e) { console.error('WebSocket connect failed:', e); }
  };

  const activeChannelRef = useRef(activeChannel);
  useEffect(() => { activeChannelRef.current = activeChannel; }, [activeChannel]);

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messaging/${orgId}/channels`, { withCredentials: true });
      setChannels(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messaging/${orgId}/unread`, { withCredentials: true });
      setUnread(res.data?.channels || {});
    } catch (err) { console.error(err); }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messaging/${orgId}/members`, { withCredentials: true });
      setOrgMembers(res.data || []);
    } catch (err) { console.error(err); }
  };

  const selectChannel = async (channel) => {
    setActiveChannel(channel);
    setView('chat');
    await fetchMessages(channel.id);
    // Start polling
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchMessages(channel.id), 5000);
  };

  const fetchMessages = async (channelId) => {
    try {
      const res = await axios.get(`${API_URL}/api/messaging/channel/${channelId}/messages`, { withCredentials: true });
      setMessages(res.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error(err); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeChannel) return;
    try {
      await axios.post(`${API_URL}/api/messaging/channel/${activeChannel.id}/messages`,
        { content: newMessage }, { withCredentials: true });
      setNewMessage('');
      fetchMessages(activeChannel.id);
    } catch (err) { console.error(err); }
  };

  const deleteMessage = async (msgId) => {
    try {
      await axios.delete(`${API_URL}/api/messaging/message/${msgId}`, { withCredentials: true });
      fetchMessages(activeChannel.id);
    } catch (err) { console.error(err); }
  };

  const createChannel = async () => {
    try {
      const payload = { type: newChannelType, name: newChannelName, member_ids: selectedMembers };
      if (newChannelType === 'team' && teams?.length > 0) payload.team_id = teams[0].id;
      const res = await axios.post(`${API_URL}/api/messaging/${orgId}/channels`, payload, { withCredentials: true });
      setShowNewChannel(false);
      setNewChannelName('');
      setSelectedMembers([]);
      fetchChannels();
      selectChannel(res.data);
    } catch (err) { console.error(err); }
  };

  const sendEmailBlast = async () => {
    setSendingBlast(true);
    try {
      const res = await axios.post(`${API_URL}/api/messaging/${orgId}/email-blast`, emailBlast, { withCredentials: true });
      alert(`Email sent to ${res.data.sent} of ${res.data.total_recipients} recipients`);
      setShowEmailBlast(false);
      setEmailBlast({ subject: '', content: '', target_type: 'org', target_id: '' });
    } catch (err) { console.error(err); alert('Failed to send emails'); }
    finally { setSendingBlast(false); }
  };

  const goBack = () => {
    setView('channels');
    setActiveChannel(null);
    if (pollRef.current) clearInterval(pollRef.current);
    fetchChannels();
    fetchUnread();
  };

  const channelIcon = (type) => {
    if (type === 'team') return <Hash className="h-4 w-4" />;
    if (type === 'direct') return <User className="h-4 w-4" />;
    if (type === 'game_chat') return <MessageCircle className="h-4 w-4" />;
    return <Users className="h-4 w-4" />;
  };

  const channelColor = (type) => {
    if (type === 'team') return 'bg-blue-500/10 text-blue-500';
    if (type === 'direct') return 'bg-green-500/10 text-green-500';
    if (type === 'game_chat') return 'bg-amber-500/10 text-amber-500';
    return 'bg-purple-500/10 text-purple-500';
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4" data-testid="messaging-panel">
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {view === 'chat' && activeChannel ? (
                <>
                  <Button size="sm" variant="ghost" onClick={goBack} data-testid="messaging-back-btn">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className={`p-1.5 rounded-full ${channelColor(activeChannel.type)}`}>
                    {channelIcon(activeChannel.type)}
                  </div>
                  {activeChannel.display_name || activeChannel.name || 'Chat'}
                  <Badge variant="outline" className="text-xs">{activeChannel.type}</Badge>
                </>
              ) : (
                <>
                  <MessageCircle className="h-5 w-5 text-blue-500" /> Messaging
                  <Badge variant="outline" className="ml-2 text-xs">{channels.length} channels</Badge>
                </>
              )}
            </CardTitle>
            {view === 'channels' && (
              <div className="flex gap-2">
                {['ad', 'company_support', 'tech_specialist', 'semprion_admin'].includes(userRole) && (
                  <Button size="sm" variant="outline" onClick={() => setView('broadcast')}
                    className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="broadcasts-btn">
                    <Mail className="h-4 w-4 mr-1" /> Broadcasts
                  </Button>
                )}
                {['ad', 'company_support'].includes(userRole) && (
                  <Button size="sm" variant="outline" onClick={() => setShowEmailBlast(true)}
                    className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="email-blast-btn">
                    <Mail className="h-4 w-4 mr-1" /> Email Blast
                  </Button>
                )}
                <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700"
                  onClick={() => { setShowNewChannel(true); fetchMembers(); }}
                  data-testid="messaging-new-btn">
                  <Plus className="h-4 w-4 mr-1" /> New
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {view === 'broadcast' && (
            <div data-testid="broadcast-view">
              <Button size="sm" variant="ghost" onClick={() => setView('channels')} data-testid="broadcast-back"><ArrowLeft className="h-4 w-4 mr-1" />Back to channels</Button>
              <CommBroadcastPanel orgId={orgId} />
            </div>
          )}
          {/* CHANNEL LIST */}
          {view === 'channels' && (
            loading ? <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading...</p> :
            channels.length > 0 ? (
              <div className="space-y-2">
                {channels.map(ch => (
                  <div key={ch.id} onClick={() => selectChannel(ch)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`channel-${ch.id}`}>
                    <div className={`p-2 rounded-full ${channelColor(ch.type)}`}>{channelIcon(ch.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {ch.display_name || ch.name || 'Unnamed'}
                        </p>
                        {unread[ch.id] > 0 && (
                          <Badge className="bg-amber-500 text-white text-xs">{unread[ch.id]}</Badge>
                        )}
                      </div>
                      <p className={`text-sm truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {ch.last_message ? `${ch.last_message.sender_name}: ${ch.last_message.content}` : 'No messages yet'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {ch.last_message?.created_at ? formatTime(ch.last_message.created_at) : ''}
                      </p>
                      <Badge variant="outline" className="text-xs mt-1">{ch.type}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No Conversations Yet</p>
                <p className="text-sm mt-1">Create a team chat, DM, or group to start</p>
              </div>
            )
          )}

          {/* CHAT VIEW */}
          {view === 'chat' && activeChannel && (
            <div className="flex flex-col" style={{ height: '400px' }}>
              <div className={`flex-1 overflow-y-auto p-3 space-y-3 border rounded-lg mb-3 ${isDark ? 'border-slate-700 bg-slate-900/30' : 'border-slate-200 bg-slate-50'}`}>
                {messages.length > 0 ? messages.map(msg => (
                  <div key={msg.id} className={`flex gap-2 group ${msg.sender_id === userId ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                      {(msg.sender_name || '?')[0].toUpperCase()}
                    </div>
                    <div className={`max-w-[70%] ${msg.sender_id === userId ? 'text-right' : ''}`}>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {msg.sender_name} <span className="ml-1">{formatTime(msg.created_at)}</span>
                      </p>
                      <div className={`inline-block px-3 py-2 rounded-lg text-sm mt-0.5 ${
                        msg.sender_id === userId
                          ? 'bg-amber-600 text-white'
                          : isDark ? 'bg-slate-700 text-white' : 'bg-white text-slate-900 border border-slate-200'
                      }`}>
                        {msg.content}
                      </div>
                      {(msg.sender_id === userId || ['ad', 'company_support', 'tech_specialist', 'semprion_admin'].includes(userRole)) && (
                        <button onClick={() => { if (msg.sender_id === userId || window.confirm('Delete this message for everyone?')) deleteMessage(msg.id); }}
                          className="opacity-0 group-hover:opacity-100 ml-2 text-red-400 hover:text-red-500 transition-opacity">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                )) : (
                  <p className={`text-center text-sm py-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No messages yet. Start the conversation!</p>
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="flex gap-2">
                <Input placeholder="Type a message..." value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  className={isDark ? 'bg-slate-800 border-slate-600 text-white' : ''}
                  data-testid="messaging-input" />
                <Button onClick={sendMessage} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="messaging-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* NEW CHANNEL DIALOG */}
      <Dialog open={showNewChannel} onOpenChange={setShowNewChannel}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>New Conversation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              {[{t: 'team', icon: Hash, label: 'Team Chat'}, {t: 'group', icon: Users, label: 'Group'}, {t: 'direct', icon: User, label: 'Direct'}].map(({t, icon: Icon, label}) => (
                <Button key={t} size="sm" variant={newChannelType === t ? 'default' : 'outline'}
                  onClick={() => setNewChannelType(t)}
                  className={newChannelType === t ? 'bg-amber-600 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}>
                  <Icon className="h-4 w-4 mr-1" /> {label}
                </Button>
              ))}
            </div>
            {newChannelType !== 'direct' && (
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Channel Name</Label>
                <Input value={newChannelName} onChange={e => setNewChannelName(e.target.value)}
                  placeholder={newChannelType === 'team' ? 'e.g., Varsity Boys Chat' : 'e.g., Coaches Group'}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
              </div>
            )}
            {(newChannelType === 'direct' || newChannelType === 'group') && (
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Select Members</Label>
                <div className={`max-h-40 overflow-y-auto border rounded p-2 space-y-1 ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                  {orgMembers.map(m => (
                    <label key={m.id} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={selectedMembers.includes(m.id)}
                        onChange={e => {
                          if (newChannelType === 'direct') setSelectedMembers(e.target.checked ? [m.id] : []);
                          else setSelectedMembers(e.target.checked ? [...selectedMembers, m.id] : selectedMembers.filter(id => id !== m.id));
                        }} />
                      <span className={`text-sm ${isDark ? 'text-slate-300' : ''}`}>{m.name}</span>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({m.roles?.join(', ')})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={createChannel} className="w-full bg-amber-600 text-white hover:bg-amber-700" data-testid="create-channel-btn">
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EMAIL BLAST DIALOG */}
      <Dialog open={showEmailBlast} onOpenChange={setShowEmailBlast}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-blue-500" /> Email Blast</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Send To</Label>
              <select value={emailBlast.target_type} onChange={e => setEmailBlast({...emailBlast, target_type: e.target.value, target_id: ''})}
                className={`w-full rounded px-3 py-2 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} data-testid="blast-target">
                <option value="org">Everyone in Organization</option>
                <option value="team">Specific Team</option>
                <option value="coaches">All Coaches</option>
                <option value="families">All Families</option>
              </select>
              {emailBlast.target_type === 'team' && (
                <select value={emailBlast.target_id} onChange={e => setEmailBlast({...emailBlast, target_id: e.target.value})}
                  className={`w-full rounded px-3 py-2 border text-sm mt-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}>
                  <option value="">Select Team</option>
                  {(teams || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Subject</Label>
              <Input value={emailBlast.subject} onChange={e => setEmailBlast({...emailBlast, subject: e.target.value})}
                placeholder="e.g., Practice Schedule Update" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="blast-subject" />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Message</Label>
              <textarea value={emailBlast.content} onChange={e => setEmailBlast({...emailBlast, content: e.target.value})}
                placeholder="Type your message here..." rows={5}
                className={`w-full rounded px-3 py-2 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} data-testid="blast-content" />
            </div>
            <Button onClick={sendEmailBlast} disabled={!emailBlast.subject.trim() || !emailBlast.content.trim() || sendingBlast}
              className="w-full bg-blue-600 text-white hover:bg-blue-700" data-testid="send-blast-btn">
              {sendingBlast ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending...</> : <><Send className="h-4 w-4 mr-1" /> Send Email</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
